import { Button, HStack, Select, Text, useToast, useDisclosure } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from '../../../../../models/hooks';
import colors from '../../../../../styles/colors';
import { FollowStateEnum } from '../../recruiterWatchAndFollow/types';
import { useCurrentUserProfile } from '../../../../../app/hooks/useCurrentUserProfile';
import { BulkProgressDialog } from '../BulkProgressDialog';
import { SelectionSummary } from './SelectionSummary';

const TOAST_ID_NO_RECRUITER = 'bulk-toast-no-recruiter';
const TOAST_ID_NO_POSITION = 'bulk-toast-no-position';

export const BulkAssignActionsContainer = React.memo(function BulkAssignActionsContainer() {
  const toast = useToast();
  const { t } = useTranslation('moniterByRecruiters');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedStatus, setSelectedStatus] = React.useState<FollowStateEnum>(FollowStateEnum.Followed);

  const {
    isBulkAssignMode,
    isAllPositionsSelected,
    selectedPositions,
    isBulkAssignInProgress,
    selectedRecruiters,
    positionResponse,
  } = useStoreState((state) => state.moniterByRecruiters);

  const { bulkUpdate, setBulkAssignMode, setIsAllPositionsSelected } = useStoreActions((state) => state.moniterByRecruiters);

  const { currentUserProfile } = useCurrentUserProfile();
  const accountId = currentUserProfile?.account ?? '';

  const showToastOnce = React.useCallback(
    (id: string, titleKey: string) => {
      if (!toast.isActive(id)) {
        toast({ id, title: t(titleKey), duration: 3000, isClosable: true });
      }
    },
    [toast, t],
  );

  const handleBulkUpdate = React.useCallback(async () => {
    if (!accountId) return;

    if (selectedRecruiters.length === 0) {
      showToastOnce(TOAST_ID_NO_RECRUITER, 'bulk.recruiterSelectToastMessage');
      return;
    }

    if (selectedPositions.length === 0 && !isAllPositionsSelected) {
      showToastOnce(TOAST_ID_NO_POSITION, 'bulk.positionSelectToastMessage');
      return;
    }

    await bulkUpdate({ accountId, followedStatus: selectedStatus }).then(onOpen);
  }, [
    accountId,
    isAllPositionsSelected,
    selectedRecruiters.length,
    selectedPositions.length,
    selectedStatus,
    bulkUpdate,
    onOpen,
    showToastOnce,
  ]);

  const handleStatusChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value as FollowStateEnum);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    onClose();
    setBulkAssignMode(false);
  }, [onClose, setBulkAssignMode]);

  const handleClearAll = React.useCallback(() => {
    setIsAllPositionsSelected(false);
  }, [setIsAllPositionsSelected]);

  const handleSelectAll = React.useCallback(() => {
    setIsAllPositionsSelected(true);
  }, [setIsAllPositionsSelected]);

  if (!isBulkAssignMode) return null;

  return (
    <>
      <HStack
        justify="space-between"
        background="#CCE6FF"
        px={4}
        py={2}
        border="1px solid"
        borderColor="#AED0FF"
        borderRadius="md"
        borderTopRadius={0}
      >
        <SelectionSummary
          selectedCount={selectedPositions.length}
          totalCount={positionResponse?.count}
          isAllSelected={isAllPositionsSelected}
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
        />

        <HStack>
          <Select
            outlineColor={colors.blue[500]}
            size="sm"
            variant="outline"
            rounded="md"
            colorScheme="blue"
            value={selectedStatus}
            onChange={handleStatusChange}
            isDisabled={isBulkAssignInProgress}
            sx={{
              borderWidth: '2px',
              fontWeight: 'bold',
              _hover: { bg: 'gray.100', borderColor: 'blue.400' },
              _focus: {
                bg: 'white',
                borderColor: 'blue.500',
                borderWidth: '2px',
                boxShadow: 'none',
              },
              _disabled: {
                bg: 'gray.200',
                cursor: 'not-allowed',
                opacity: 1,
              },
            }}
          >
            <option value="" disabled>
              {t('bulk.selectStatus.placeholder')}
            </option>
            <option value={FollowStateEnum.Followed}>{t('bulk.selectStatus.follow')}</option>
            <option value={FollowStateEnum.Watched}>{t('bulk.selectStatus.watch')}</option>
            <option value={FollowStateEnum.None}>{t('bulk.selectStatus.none')}</option>
          </Select>

          <Button
            size="sm"
            colorScheme="blue"
            px={4}
            flexShrink={0}
            isLoading={isBulkAssignInProgress}
            onClick={handleBulkUpdate}
            data-testid="confirm"
          >
            <Text lineHeight={2}>{t('bulk.confirmButton')}</Text>
          </Button>
        </HStack>
      </HStack>

      <BulkProgressDialog isOpen={isOpen} onClose={handleDialogClose} />
    </>
  );
});
